
import OrderProductIssueRow from '@marketplace/components/account/orders/order-product-issue-row.vue'
import InputTextarea from '@marketplace/components/forms/inputs/input-textarea.vue'
import OcRadio from '@marketplace/components/forms/inputs/radiobox.vue'
import HelpText from '@marketplace/components/forms/inputs/shared/help-text.vue'
import InputLabel from '@marketplace/components/forms/inputs/shared/input-label.vue'
import AnimatedCheckmark from '@marketplace/components/shared/animations/animated-checkmark.vue'
import {
    FEEDBACK_REASON,
    REASONS_WITH_REQUIRED_IMAGE,
} from '@marketplace/config/order.js'
import { ORDER_FEEDBACK_CREATE_MUTATION } from '@marketplace/graphql/feedback.js'
import { EventBus } from '@marketplace/plugins/event-bus.js'
import axios from 'axios'
import Cookies from 'js-cookie'
import cloneDeep from 'lodash/cloneDeep'
import { minValue } from 'vuelidate/lib/validators'

export default {
    name: 'ModalOrderIssue',

    components: {
        OrderProductIssueRow,
        InputTextarea,
        AnimatedCheckmark,
        OcRadio,
        InputLabel,
        HelpText,
    },

    props: {
        orderStorefronts: {
            type: Array,
            default: undefined,
        },

        productFeedbackReasons: {
            type: Array,
            default: () => [],
        },
        invoiceFeedbackReasons: {
            type: Array,
            default: undefined,
        },
        fetchMoreProducts: {
            type: Function,
            default: () => {},
        },
    },

    data() {
        return {
            message: '',
            thankyou: false,
            orderId: 'none',
            reason: 0,
            invoiceReason: 0,
            imageNotFilled: false,
            productsNotPicked: false,
            imageUrls: '',
            productIds: '',
            userErrors: '',
            loading: false,
            radioError: true,
            initialProducts: {},
            moreProductsLoading: false,
        }
    },

    computed: {
        currentOrder() {
            return (
                this.orderStorefronts.find(
                    (order) => order.node.id === this.orderId
                ) || {}
            )
        },
        disabled() {
            return (
                (!(this.reason !== null && this.message.length >= 5) &&
                    !this.isCustomerRegretIssue) ||
                !this.hasRequiredImage ||
                !this.hasRequiredProductsPicked ||
                (this.$breakpoint.includes('mobile') && this.radioError)
            )
        },
        chosenReason() {
            return this.displayedFeedbackReasons[this.reason]
        },
        chosenInvoiceReason() {
            return this.hasInvoiceIssue
                ? this.displayedInvoiceFeedbackReasons[
                      this.invoiceReason
                  ]?.toUpperCase()
                : ''
        },
        orderFeedbackCreateInput() {
            return {
                orderId: this.orderId,
                reason: FEEDBACK_REASON[this.chosenReason],
                invoiceReason: this.chosenInvoiceReason || null,
                message: this.message,
                ...(this.selectedProductsList && {
                    affectedProductQuantities: this.selectedProductsList,
                }),
                ...(this.imageUrls && { files: this.imageUrls }),
            }
        },
        requiresImage() {
            return REASONS_WITH_REQUIRED_IMAGE.includes(
                this.chosenReason?.toUpperCase()
            )
        },
        hasRequiredImage() {
            return this.requiresImage ? Boolean(this.imageUrls) : true
        },
        displayedOrderStorefronts() {
            return [
                {
                    node: {
                        id: 'none',
                        name: this.$t(
                            'account.orderpage.modal.issue.select_brand'
                        ),
                    },
                },
                ...this.orderStorefronts,
            ]
        },
        displayedFeedbackReasons() {
            if (
                !this.currentOrder ||
                !this.currentOrder.node ||
                !this.currentOrder.node.feedbackReasons
            ) {
                return []
            }

            if (this.$breakpoint.includes('mobile')) {
                return this.currentOrder.node.feedbackReasons
            }

            return [
                this.$t('account.orderpage.modal.issue.select_issue'),
                ...this.currentOrder.node.feedbackReasons.filter(
                    (reason) => reason !== 'has_defective'
                ),
            ]
        },

        displayedInvoiceFeedbackReasons() {
            return [
                this.$t('account.orderpage.modal.issue.select_invoice_issue'),
                ...this.invoiceFeedbackReasons,
            ]
        },
        productsList() {
            const orderData = this.orderStorefronts
            const productsArray = []
            orderData.map((order) => {
                order.node.products.edges.map((product) => {
                    if (order.node.id === this.orderId) {
                        productsArray.push(product.node)
                    }
                })
            })
            return productsArray
        },
        selectedProductsList() {
            return this.productsList
                .filter((product) => product.isSelected)
                .map((product) => {
                    return { id: product.id, quantity: product.issueQuantity }
                })
        },
        hasMultipleSupplierStorefronts() {
            return this.orderStorefronts.length > 1
        },
        trackedShipments() {
            return this.orderStorefronts
                .map((storefront) => storefront.node.shipments.edges)
                .flat()
                .map((item) => item.node)
                .filter(
                    (shipment) =>
                        shipment.trackingUrl || shipment.trackingNumber
                )
        },
        trackedShipmentsUrls() {
            return this.trackedShipments.filter(
                (shipment) => shipment.trackingUrl
            )
        },
        hasTrackedShipments() {
            return this.trackedShipments.length > 0
        },
        hasInvoiceIssue() {
            return (
                FEEDBACK_REASON[this.chosenReason] ===
                FEEDBACK_REASON.has_invoice_problem
            )
        },
        isCustomerRegretIssue() {
            return (
                FEEDBACK_REASON[this.chosenReason] ===
                FEEDBACK_REASON.has_customer_regret_return
            )
        },
        hasRequiredInvoiceIssuePicked() {
            return this.hasInvoiceIssue ? Boolean(this.invoiceReason) : true
        },
        hasProductIssue() {
            return this.productFeedbackReasons.find(
                (reason) => reason === this.chosenReason
            )
        },
        hasRequiredProductsPicked() {
            return this.hasProductIssue
                ? Boolean(this.selectedProductsList.length)
                : true
        },
        showSelectBrandWarning() {
            const reasonsToBeCheckedForSelectedBrand = [4, 5, 7, 10]

            if (this.orderId !== 'none') {
                return false
            }

            if (
                this.hasMultipleSupplierStorefronts &&
                reasonsToBeCheckedForSelectedBrand.includes(
                    parseInt(this.reason)
                )
            ) {
                return true
            }

            return false
        },
    },

    validations() {
        return {
            orderId: {
                function(value) {
                    return value !== 'none'
                },
            },
            reason: {
                minValue: minValue(1),
            },
            invoiceReason: {
                minValue: this.hasInvoiceIssue ? minValue(1) : 0,
            },
        }
    },
    watch: {
        reason() {
            this.resetSelectedProductsList()
        },
    },

    created() {
        this.captureInitialState()
    },

    mounted() {
        if (!this.hasMultipleSupplierStorefronts) {
            this.orderId = this.orderStorefronts[0]?.node.id
        }
        EventBus.$on('modal-closed', this.onModalClosed)
    },

    beforeDestroy() {
        this.productsList
            .filter((product) => product.isSelected)
            .map((product) => {
                this.$set(product, 'isSelected', false)
                this.$set(product, 'issueQuantity', 0)
            })
        EventBus.$off('modal-closed', this.onModalClosed)
    },

    methods: {
        async loadMoreHandler() {
            this.moreProductsLoading = true

            if (!this.currentOrder || !this.currentOrder.node) {
                console.error('Selected storefront not found')
                this.moreProductsLoading = false
                return
            }

            const endCursor = this.currentOrder.node.products.pageInfo.endCursor

            try {
                const response = await this.fetchMoreProducts(endCursor)

                if (response && response.data && response.data.order) {
                    const tempProductDetails =
                        response.data.order.supplierOrders.edges.find(
                            (edge) => edge.node.id === this.orderId
                        )?.node.products

                    if (tempProductDetails) {
                        // Merge new products with existing ones
                        const updatedProducts = [
                            ...this.currentOrder.node.products.edges,
                            ...tempProductDetails.edges,
                        ]

                        // Update the current order's products list with new products
                        this.$set(
                            this.currentOrder.node.products,
                            'edges',
                            updatedProducts
                        )

                        // Update pageInfo for future fetches
                        this.$set(
                            this.currentOrder.node.products,
                            'pageInfo',
                            tempProductDetails.pageInfo
                        )
                    }
                }
            } catch (error) {
                this.$sentry.captureException(error)
            } finally {
                this.moreProductsLoading = false
            }
        },

        onModalClosed() {
            this.orderStorefronts?.forEach((storefront) => {
                const storefrontId = storefront.node.id
                const initialProductsForStorefront =
                    this.initialProducts[storefrontId]

                if (initialProductsForStorefront) {
                    this.$set(
                        storefront.node,
                        'products',
                        cloneDeep(initialProductsForStorefront)
                    )
                }
            })
        },

        captureInitialState() {
            this.orderStorefronts?.forEach((storefront) => {
                const { id, products } = storefront.node
                this.initialProducts[id] = {
                    edges: cloneDeep(products.edges),
                    pageInfo: cloneDeep(products.pageInfo),
                }
            })
        },

        resetSelectedProductsList() {
            this.productsList
                .filter((product) => product.isSelected)
                .map((product) => {
                    this.$set(product, 'isSelected', false)
                    this.$set(product, 'issueQuantity', 0)
                })
        },
        handleClick() {
            this.$v.$touch()
            if (!this.hasRequiredImage) {
                this.imageNotFilled = true
            }
            if (!this.hasRequiredProductsPicked) {
                this.productsNotPicked = true
            }
            if (
                !this.$v.$invalid &&
                this.hasRequiredImage &&
                this.hasRequiredProductsPicked
            ) {
                this.loading = true

                this.$apollo
                    .mutate({
                        mutation: ORDER_FEEDBACK_CREATE_MUTATION,
                        variables: {
                            orderFeedbackCreateInput:
                                this.orderFeedbackCreateInput,
                        },
                    })
                    .then((res) => {
                        this.userErrors = ''
                        if (res.data.orderFeedbackCreate.userErrors?.length) {
                            this.userErrors =
                                res.data.orderFeedbackCreate.userErrors
                                    .map((err) => err.message)
                                    .join(', ')
                            return false
                        }
                        this.thankyou = true
                        setTimeout(() => {
                            EventBus.$emit('modal-close')
                            window.location.reload()
                        }, 2000)
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        },
        getOrderStorefrontName(order, index) {
            if (order.id === 'none') {
                return order.name
            }
            return (
                order.storefronts?.edges[0]?.node?.name ||
                `Storefront #${index}`
            )
        },
        updateSelectedList(payload) {
            this.productsNotPicked = false
            this.productsList.find((product) => {
                if (product.id === payload.id) {
                    this.$set(product, 'isSelected', payload.isSelected)
                    this.$set(product, 'issueQuantity', payload.quantity)
                }
            })
        },
        async uploadImage(e) {
            const sendData = new FormData()
            const filesLength = e.target.files.length

            for (let i = 0; i < filesLength; i++) {
                sendData.append('files[]', e.target.files[i])
            }

            this.userErrors = ''

            // Adding a breadcrumb before starting the image upload
            this.$sentry.addBreadcrumb({
                category: 'order-issue-upload',
                message: 'Starting image upload',
                data: {
                    csrfToken:
                        window.Store?.csrfToken ?? Cookies.get('XSRF-TOKEN'),
                    files: e.target.files,
                    filesLength,
                },
                level: 'info',
            })

            try {
                const response = await axios({
                    headers: {
                        'X-Csrf-Token':
                            window.Store?.csrfToken ??
                            Cookies.get('XSRF-TOKEN'),
                        'Content-Type': 'multipart/form-data',
                        Accept: 'application/json',
                    },
                    method: 'post',
                    url: '/api/v1/file/upload/image',
                    data: sendData,
                })

                this.imageNotFilled = false

                if (response?.data?.files) {
                    this.imageUrls = response.data.files
                } else {
                    this.userErrors = this.$t('labels.error_uploading_file')
                    this.$sentry.captureException(
                        new Error('Issue image upload failed'),
                        {
                            extra: {
                                response,
                            },
                        }
                    )
                }
            } catch (error) {
                const errorMessage = error.response?.data?.errors?.['files.0']

                this.userErrors =
                    errorMessage || this.$t('labels.error_uploading_file')
                this.$sentry.captureException(error, {
                    extra: {
                        sendData,
                        error: error.response?.data?.errors,
                        url: '/api/v1/file/upload/image',
                    },
                })
            }
        },
        updateCurrentReason(payload) {
            this.reason = payload.value
            this.radioError = !payload.value
        },
    },
}
